import { StateCreator } from 'zustand';
import { getPatients } from '../../services/api';
import type {
  EpisodeNotePreview,
  PatientManagerSortBy,
  PatientResponse as ApiPatientResponse,
} from '../../services/api/types.gen';
import type {
  ExternalEmrListFilter,
  ExternalEmrListStatusFilter,
  ExternalEmrPatientState,
  ExternalEmrStatusCounts,
} from '../../services/externalEmrPatients';

export interface Patient {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  mrn: string;
  // Backend fields
  first_name: string;
  last_name: string;
  date_of_birth: string;
  ssn?: string;
  emergency_contact?: string;
  emergency_phone?: string;
  needs_legal_representative: boolean;
  has_poa: boolean;
  is_test_patient?: boolean;
  // Imported-chart provenance (EMR-1177): source + timestamp are permanent;
  // hidden_at set means the badge was demoted and must not render.
  import_source?: string;
  imported_at?: string;
  import_badge_hidden_at?: string;
  insurance_name?: string;
  clinic_id?: string;
  clinic_name?: string;
  /**
   * When the list request that returned this row began (ms, client clock).
   * The import status store ignores a row older than its own newest status.
   */
  listed_at?: number;
  /** Shell or imported External EMR patient; absent for a native patient. */
  external_emr?: ExternalEmrPatientState | null;
  /** Lines in this chart's sync review nobody has closed (the header count). */
  sync_review_new_count?: number;
  /** Current episode (active today, else the most recent): see T1 contract. */
  episode_start?: string | null;
  episode_end?: string | null;
  primary_dx_code?: string | null;
  primary_dx_description?: string | null;
  /** Newest first. */
  episode_notes_preview: EpisodeNotePreview[];
  created_at: string;
  updated_at: string;
}

export type PatientManagerSortOrder = 'asc' | 'desc';

export interface PatientManagerQuery {
  page?: number;
  search?: string;
  clinic_id?: string | null;
  surname_initial?: string | null;
  show_inactive?: boolean;
  hide_test?: boolean;
  hide_imported_patients?: boolean;
  /** Table column sort; null is the server default (last name, first name). */
  sort_by?: PatientManagerSortBy | null;
  sort_order?: PatientManagerSortOrder | null;
  /** Current-episode payer ids; empty means every payer. */
  insurance_ids?: string[];
  /** Exact city names; empty means every city. */
  cities?: string[];
  /** Distinct cities of the population, for the City filter. Default: page 1. */
  include_city_options?: boolean;
  /**
   * External EMR view: list shells and/or imported patients. Null is the
   * ordinary Patient Manager.
   */
  external_emr?: ExternalEmrListFilter | null;
  /** External EMR view only: the status chip pressed. */
  external_emr_status?: ExternalEmrListStatusFilter | null;
  include_metadata?: boolean;
  /**
   * Refresh without the loading state, so the grid stays on screen (an
   * import settling on a card). Not sent to the server.
   */
  background?: boolean;
}

export interface PatientsSlice {
  patients: Patient[];
  isPatientsLoading: boolean;
  error: string | null;
  searchTerm: string;
  totalPatients: number;
  alphaCounts: Record<string, number>;
  currentPage: number;
  pageSize: number;
  clinicId: string | null;
  surnameInitial: string;
  showInactive: boolean;
  hideTest: boolean;
  patientManagerHideImportedPatients: boolean;
  /** Table sort. Persisted with the other Patient Manager preferences. */
  patientManagerSortBy: PatientManagerSortBy | null;
  patientManagerSortOrder: PatientManagerSortOrder;
  /** Insurance and City filters. Not persisted: the page opens unfiltered. */
  patientManagerInsuranceIds: string[];
  patientManagerCities: string[];
  /** City options from the last page-1 response (the accessible population). */
  patientManagerCityOptions: string[];
  /** The population (clinic, inactive, test, imported, view) those options describe. */
  patientManagerCityOptionsKey: string | null;
  /** Roger vs External EMR list. Not persisted: the page opens on Roger charts. */
  patientManagerExternalEmrView: boolean;
  /** The External EMR table's status chip; cleared with the view. */
  patientManagerExternalEmrStatus: ExternalEmrListStatusFilter | null;
  /** What each status chip would list, from the last External EMR page 1. */
  externalEmrStatusCounts: ExternalEmrStatusCounts | null;
  setSearchTerm: (term: string) => void;
  setCurrentPage: (page: number) => void;
  setClinicId: (clinicId: string | null) => void;
  setSurnameInitial: (surnameInitial: string) => void;
  setShowInactive: (showInactive: boolean) => void;
  setHideTest: (hideTest: boolean) => void;
  setPatientManagerHideImportedPatients: (hide: boolean) => void;
  setPatientManagerSort: (
    sortBy: PatientManagerSortBy | null,
    sortOrder?: PatientManagerSortOrder
  ) => void;
  setPatientManagerInsuranceIds: (ids: string[]) => void;
  setPatientManagerCities: (cities: string[]) => void;
  setPatientManagerExternalEmrView: (on: boolean) => void;
  setPatientManagerExternalEmrStatus: (
    status: ExternalEmrListStatusFilter | null
  ) => void;
  fetchPatients: (query?: PatientManagerQuery) => Promise<void>;
}

// Helper function to convert API response to frontend Patient format
const convertApiPatientToPatient = (
  apiPatient: ApiPatientResponse
): Patient => {
  if (!apiPatient) {
    console.error('Received null/undefined patient data');
    throw new Error('Invalid patient data received from API');
  }

  return {
    id: apiPatient.id || '',
    name: `${apiPatient.first_name || ''} ${apiPatient.last_name || ''}`.trim(),
    email: apiPatient.email || undefined,
    phone: apiPatient.phone || undefined,
    address: apiPatient.address || undefined,
    city: apiPatient.city || undefined,
    state: apiPatient.state || undefined,
    zip_code: apiPatient.zip_code || undefined,
    mrn: apiPatient.mrn || 'N/A',
    first_name: apiPatient.first_name || '',
    last_name: apiPatient.last_name || '',
    date_of_birth: apiPatient.date_of_birth || '',
    ssn: apiPatient.ssn || undefined,
    emergency_contact: apiPatient.emergency_contact || undefined,
    emergency_phone: apiPatient.emergency_phone || undefined,
    needs_legal_representative: apiPatient.needs_legal_representative || false,
    has_poa: apiPatient.has_poa || false,
    is_test_patient: apiPatient.is_test_patient || false,
    import_source: apiPatient.import_source || undefined,
    imported_at: apiPatient.imported_at || undefined,
    import_badge_hidden_at: apiPatient.import_badge_hidden_at || undefined,
    insurance_name: apiPatient.insurance_name || undefined,
    clinic_id: apiPatient.clinic_id || undefined,
    clinic_name: apiPatient.clinic_name || undefined,
    external_emr: apiPatient.external_emr ?? null,
    sync_review_new_count: apiPatient.sync_review_new_count ?? 0,
    episode_start: apiPatient.episode_start ?? null,
    episode_end: apiPatient.episode_end ?? null,
    primary_dx_code: apiPatient.primary_dx_code ?? null,
    primary_dx_description: apiPatient.primary_dx_description ?? null,
    episode_notes_preview: apiPatient.episode_notes_preview ?? [],
    created_at: apiPatient.created_at || '',
    updated_at: apiPatient.updated_at || '',
  };
};

// Monotonic id for in-flight fetchPatients calls — see the staleness guard
// inside fetchPatients.
let fetchPatientsSeq = 0;

export const patientsSlice: StateCreator<PatientsSlice> = (set, get) => ({
  patients: [],
  isPatientsLoading: false,
  error: null,
  searchTerm: '',
  totalPatients: 0,
  alphaCounts: {},
  currentPage: 1,
  pageSize: 50,
  clinicId: null,
  surnameInitial: '',
  showInactive: false,
  hideTest: true,
  patientManagerHideImportedPatients: true,
  patientManagerSortBy: null,
  patientManagerSortOrder: 'asc',
  patientManagerInsuranceIds: [],
  patientManagerCities: [],
  patientManagerCityOptions: [],
  patientManagerCityOptionsKey: null,
  patientManagerExternalEmrView: false,
  patientManagerExternalEmrStatus: null,
  externalEmrStatusCounts: null,

  setSearchTerm: (term: string) => {
    set({ searchTerm: term });
  },

  setCurrentPage: (currentPage: number) => {
    set({ currentPage });
  },

  setClinicId: (clinicId: string | null) => {
    set({ clinicId });
  },

  setSurnameInitial: (surnameInitial: string) => {
    set({ surnameInitial });
  },

  setShowInactive: (showInactive: boolean) => {
    set({ showInactive });
  },

  setHideTest: (hideTest: boolean) => {
    set({ hideTest });
  },

  setPatientManagerHideImportedPatients: (hide: boolean) => {
    set({ patientManagerHideImportedPatients: hide });
  },

  setPatientManagerSort: (sortBy, sortOrder = 'asc') => {
    set({
      patientManagerSortBy: sortBy,
      patientManagerSortOrder: sortOrder,
    });
  },

  setPatientManagerInsuranceIds: ids => {
    set({ patientManagerInsuranceIds: ids });
  },

  setPatientManagerCities: cities => {
    set({ patientManagerCities: cities });
  },

  setPatientManagerExternalEmrView: (on: boolean) => {
    // The chip belongs to the view: turning it off clears the chip.
    set(
      on
        ? { patientManagerExternalEmrView: true }
        : {
            patientManagerExternalEmrView: false,
            patientManagerExternalEmrStatus: null,
            externalEmrStatusCounts: null,
          }
    );
  },

  setPatientManagerExternalEmrStatus: status => {
    set({ patientManagerExternalEmrStatus: status });
  },

  fetchPatients: async (query = {}) => {
    const background = query.background === true;
    const state = get();
    const page = query.page ?? state.currentPage;
    const currentQuery = {
      page,
      search: query.search ?? state.searchTerm,
      // `null` deliberately clears an active server filter; only an omitted
      // key inherits the previous query state.
      clinic_id:
        query.clinic_id !== undefined ? query.clinic_id : state.clinicId,
      surname_initial:
        query.surname_initial !== undefined
          ? query.surname_initial
          : state.surnameInitial || null,
      show_inactive: query.show_inactive ?? state.showInactive,
      hide_test: query.hide_test ?? state.hideTest,
      hide_imported_patients:
        query.hide_imported_patients ??
        state.patientManagerHideImportedPatients,
      sort_by:
        query.sort_by !== undefined
          ? query.sort_by
          : state.patientManagerSortBy,
      sort_order: query.sort_order ?? state.patientManagerSortOrder,
      insurance_ids: query.insurance_ids ?? state.patientManagerInsuranceIds,
      cities: query.cities ?? state.patientManagerCities,
      // Only the caller that shows the External EMR view asks for it. A stored
      // fallback would leak the view into any other fetch (or another agency).
      external_emr: query.external_emr ?? null,
      external_emr_status: query.external_emr
        ? (query.external_emr_status ?? null)
        : null,
      include_metadata: query.include_metadata ?? page === 1,
    };
    // The city options describe the population, not the page: ask again only
    // when that changes or while none are known.
    const cityOptionsKey = [
      currentQuery.clinic_id ?? '',
      currentQuery.show_inactive,
      currentQuery.hide_test,
      currentQuery.hide_imported_patients,
      currentQuery.external_emr ?? '',
    ].join('|');
    const includeCityOptions =
      query.include_city_options ??
      (page === 1 &&
        (state.patientManagerCityOptions.length === 0 ||
          cityOptionsKey !== state.patientManagerCityOptionsKey));
    set({
      currentPage: currentQuery.page,
      clinicId: currentQuery.clinic_id,
      surnameInitial: currentQuery.surname_initial ?? '',
      searchTerm: currentQuery.search,
      showInactive: currentQuery.show_inactive,
      hideTest: currentQuery.hide_test,
      patientManagerHideImportedPatients: currentQuery.hide_imported_patients,
      patientManagerSortBy: currentQuery.sort_by,
      patientManagerSortOrder: currentQuery.sort_order,
      patientManagerInsuranceIds: currentQuery.insurance_ids,
      patientManagerCities: currentQuery.cities,
    });
    // Concurrent fetches (e.g. the mount fetch racing a filter-toggle fetch)
    // resolve in any order; only the newest request may write results, or a
    // stale response overwrites the filter the user just applied.
    const seq = ++fetchPatientsSeq;
    try {
      if (background) set({ error: null });
      else set({ isPatientsLoading: true, error: null });
      // The External EMR view is the census, not a Roger-chart filter. It
      // lists every External EMR patient, so inactive, test, and imported
      // stay in. Those preferences are already saved above for Roger charts.
      // An absent `external_emr` is today's request.
      const {
        external_emr: externalEmr,
        external_emr_status: externalEmrStatus,
        sort_by: sortBy,
        sort_order: sortOrder,
        insurance_ids: insuranceIds,
        cities,
        ...baseQuery
      } = currentQuery;
      const query = externalEmr
        ? {
            ...baseQuery,
            include_city_options: includeCityOptions,
            show_inactive: true,
            hide_test: false,
            hide_imported_patients: false,
            external_emr: externalEmr,
            ...(externalEmrStatus
              ? { external_emr_status: externalEmrStatus }
              : {}),
          }
        : {
            ...baseQuery,
            include_city_options: includeCityOptions,
            // Sort and the table filters go only when set: unsorted and
            // unfiltered is today's request byte for byte.
            ...(sortBy ? { sort_by: sortBy, sort_order: sortOrder } : {}),
            ...(insuranceIds.length > 0 ? { insurance_ids: insuranceIds } : {}),
            ...(cities.length > 0 ? { cities } : {}),
          };
      const requestedAt = Date.now();
      const response = await getPatients({ query });
      if (seq !== fetchPatientsSeq) {
        return;
      }
      if (response && response.data) {
        const patients = response.data.items.map(item => ({
          ...convertApiPatientToPatient(item),
          listed_at: requestedAt,
        }));
        const metadata =
          response.data.total != null && response.data.alpha_counts != null
            ? {
                totalPatients: response.data.total,
                alphaCounts: response.data.alpha_counts,
              }
            : {};
        // A response without counts keeps the last ones; leaving the view
        // clears them (setPatientManagerExternalEmrView).
        const statusCounts = response.data.external_emr_status_counts
          ? {
              externalEmrStatusCounts: response.data.external_emr_status_counts,
            }
          : {};
        // A page-only response carries no options; the last ones stay.
        const cityOptions = response.data.city_options
          ? {
              patientManagerCityOptions: response.data.city_options,
              patientManagerCityOptionsKey: cityOptionsKey,
            }
          : {};
        set({
          patients,
          ...metadata,
          ...statusCounts,
          ...cityOptions,
          currentPage: response.data.page,
          pageSize: response.data.page_size,
          isPatientsLoading: false,
        });
      } else {
        // The generated client doesn't throw on HTTP errors; without this
        // branch a failed load leaves stale data and no error signal.
        set({
          error: 'Failed to load patients. Please try again.',
          patients: [],
          totalPatients: 0,
          alphaCounts: {},
        });
      }
    } catch (error) {
      if (seq !== fetchPatientsSeq) {
        return;
      }
      console.error('Error fetching patients:', error);
      set({
        error: 'Failed to load patients. Please try again.',
        patients: [],
        totalPatients: 0,
        alphaCounts: {},
      });
    } finally {
      if (seq === fetchPatientsSeq) {
        set({ isPatientsLoading: false });
      }
    }
  },
});
